import React, { useCallback, useEffect, useState } from 'react';
import {
  type Checklist,
  type ChecklistItem,
  ITEM_STATUS_LABEL,
  STORE_STATUS_LABEL,
  itemStatusTone,
  storeStatusTone,
} from '../../features/onboarding/onboardingChecklist';
import {
  Button,
  Callout,
  ConfirmDialog,
  Input,
  Spinner,
  StatusLabel,
} from '../../ui/v2';
import { api, apiEndpoints } from '../../utils/apiService';

type Action = 'mark_done' | 'reopen' | 'mark_live' | 'reset';

/**
 * The store's onboarding checklist as our team sees it: every item with its
 * status, "Mark done" (with an optional note the store sees) for anything
 * we finish by hand, "Reopen", "Mark live", and "Undo go live" for a store
 * marked Live by mistake. `onChange` gets the checklist
 * after each saved change, so the table behind the dialog can follow.
 */
export default function OnboardingChecklistAdmin({
  storeId,
  onChange,
}: {
  storeId: number;
  onChange?: (checklist: Checklist) => void;
}) {
  const [checklist, setChecklist] = useState<Checklist | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [confirmingReset, setConfirmingReset] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const response = await api.get(apiEndpoints.onboardingChecklist(storeId));
      if (!response?.ok) throw new Error();
      setChecklist((await response.json()) as Checklist);
    } catch {
      setError('Failed to load the onboarding checklist');
    }
  }, [storeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (key: string, action: Action) => {
    setBusy(`${key}:${action}`);
    setError('');
    try {
      const response = await api.post(
        apiEndpoints.onboardingChecklistItem(key, storeId),
        { action, note: notes[key] ?? '' },
      );
      if (!response?.ok) throw new Error();
      const updated = (await response.json()) as Checklist;
      setChecklist(updated);
      onChange?.(updated);
    } catch {
      setError('That change didn’t save');
    } finally {
      setBusy(null);
    }
  };

  if (!checklist) {
    return error ? (
      <Callout tone="danger">{error}</Callout>
    ) : (
      <div role="status" className="flex items-center gap-2 text-v2-13">
        <Spinner />
        Loading checklist...
      </div>
    );
  }

  const live = checklist.status === 'live';
  const actionFor = (item: ChecklistItem) => {
    if (item.key === 'go_live') {
      return (
        <>
          {!live && (
            <Button
              size="sm"
              loading={busy === 'go_live:mark_live'}
              onClick={() => void act('go_live', 'mark_live')}
            >
              Mark live
            </Button>
          )}
          {(live || checklist.go_live_confirmed_at) && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setConfirmingReset(true)}
            >
              Undo go live
            </Button>
          )}
        </>
      );
    }
    return (
      <>
        {item.status !== 'done' && (
          <>
            <Input
              aria-label={`Note for ${item.title}`}
              placeholder="Note (optional, the store sees it)"
              value={notes[item.key] ?? ''}
              onChange={(e) =>
                setNotes((current) => ({
                  ...current,
                  [item.key]: e.target.value,
                }))
              }
              size="sm"
              className="w-56"
            />
            <Button
              size="sm"
              variant="primary"
              loading={busy === `${item.key}:mark_done`}
              onClick={() => void act(item.key, 'mark_done')}
            >
              Mark done
            </Button>
          </>
        )}
        {item.state && (
          <Button
            size="sm"
            variant="ghost"
            loading={busy === `${item.key}:reopen`}
            onClick={() => void act(item.key, 'reopen')}
          >
            Reopen
          </Button>
        )}
      </>
    );
  };

  return (
    <section aria-labelledby="onboarding-checklist-heading">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h3
          id="onboarding-checklist-heading"
          className="text-v2-13 font-semibold text-v2-fg"
        >
          What the store sees
        </h3>
        <StatusLabel tone={storeStatusTone(checklist.status)}>
          {STORE_STATUS_LABEL[checklist.status]}
        </StatusLabel>
        <span className="text-v2-12 text-v2-fg-3">
          {checklist.counts.done} of {checklist.counts.total} done
        </span>
      </div>
      {error && <Callout tone="danger">{error}</Callout>}
      <ul className="flex flex-col">
        {checklist.items.map((item) => (
          <li
            key={item.key}
            className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-v2-line py-2.5 first:border-t-0"
          >
            <div className="min-w-0 flex-[1_1_260px] text-v2-13">
              <div className="font-medium text-v2-fg">{item.title}</div>
              <div className="text-v2-12 text-v2-fg-3">
                {item.detail}
                {item.note && ` · Note: ${item.note}`}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <StatusLabel tone={itemStatusTone(item.status)}>
                {ITEM_STATUS_LABEL[item.status]}
              </StatusLabel>
              {actionFor(item)}
            </div>
          </li>
        ))}
      </ul>
      {checklist.test_feedback && checklist.test_feedback.length > 0 && (
        <div className="mt-3">
          <h4 className="mb-1.5 text-v2-12 font-semibold text-v2-fg">
            Test feedback from the store
          </h4>
          <ul className="flex flex-col gap-1.5">
            {checklist.test_feedback.map((feedback) => (
              <li key={feedback.id}>
                <Callout tone="warning">
                  {feedback.message}
                  <span className="mt-0.5 block text-v2-12 text-v2-fg-3">
                    {new Date(feedback.created_at).toLocaleString()}
                    {feedback.call_id && ` · ${feedback.call_id}`}
                  </span>
                </Callout>
              </li>
            ))}
          </ul>
        </div>
      )}
      <ConfirmDialog
        open={confirmingReset}
        onClose={() => setConfirmingReset(false)}
        onConfirm={async () => {
          await act('go_live', 'reset');
          setConfirmingReset(false);
        }}
        busy={busy === 'go_live:reset'}
        title="Undo go live?"
        consequence="The store goes back to before it confirmed going live, and calls so far stop counting toward Live. When it went live isn't kept."
        confirmLabel="Undo go live"
      />
    </section>
  );
}
